import { useNavigate } from "react-router-dom";
import { ROUTE } from "../../routes";
import styled from "styled-components";
import { Loader } from "../../components/Loader";
import { Flex } from "../../components/Flex";
import { Box } from "../../components/Box";
import { H2 } from "../../components/Heading";
import { Button } from "../../components/Button";
import { FaPlus } from "react-icons/fa";
import { ApplicationTypeCard } from "../../components/ApplicationTypeCard";
import {
  MandateTypeScope,
  useMandateTypes,
} from "../../data/useMandateTypes";
import { useDivisions } from "../../data/useDivisions";
import Select, { Option } from "../../components/Select";
import { MandateType } from "../../types/MandateType";
import { useBillingInfo } from "../../data/useBillingInfo";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { Icon } from "../../components/Icon";
import { Tooltip } from "../../components/Tooltip";
import { Paragraph } from "../../components/Paragraph";
import { Link } from "../../components/Link";
import { env } from "../../utils/env";
import { useEffect, useMemo, useState } from "react";
import CustomTabs from "../../components/CustomTabs";
import { HiOutlineUser, HiOutlineBuildingOffice2 } from "react-icons/hi2";

const Grid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  ${({ theme: { space } }) => `
    grid-gap: ${space[3]};
    overflow: hidden;
    margin-top: 32px;
  `}
`;

type TypeFilter = "individual" | "entity";
const SettingsApplicationTypes = () => {
  const navigate = useNavigate();

  // Division filter: `?divisions=1,2,3` (Application Types have no Teams).
  const [divisionIds, setDivisionIds] = useState<number[]>([]);

  const { data: divisionsQuery } = useDivisions();

  const divisionOptions = useMemo<Option[]>(
    () =>
      (divisionsQuery?.data ?? [])
        .filter((d) => d.is_active)
        .map((d) => ({ value: d.id, label: d.display_name })),
    [divisionsQuery?.data],
  );
  const scope = useMemo<MandateTypeScope>(
    () => ({ divisions: divisionIds }),
    [divisionIds],
  );
  const hasScopeFilter = divisionIds.length > 0;

  const { data: billingData, isLoading: billingInfoLoading } = useBillingInfo();

  const billingInfo =
    billingData?.data?.results?.length > 0
      ? billingData?.data?.results[0]
      : null;

  const profilesEnabled = Boolean(billingInfo?.profiles_enabled);

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!profilesEnabled && activeTab !== 0) setActiveTab(0);
  }, [profilesEnabled, activeTab]);

  const viewType: TypeFilter =
    profilesEnabled && activeTab === 1 ? "entity" : "individual";

  const { data: mandateTypeQuery, isLoading: mandateLoading } =
    useMandateTypes(viewType, scope);

  const mandateTypes = useMemo<MandateType[]>(
    () => (mandateTypeQuery?.data ?? []) as MandateType[],
    [mandateTypeQuery?.data],
  );

  const filtered = useMemo(
    () => mandateTypes.filter((m: any) => m?._type === viewType),
    [mandateTypes, viewType],
  );

  const isLoading = mandateLoading || billingInfoLoading;

  const tabs = useMemo(() => {
    const base = [
      {
        label: (
          <>
            <HiOutlineUser size={16} />
            <span>Individual</span>
          </>
        ),
        content: null,
      },
    ];

    if (!profilesEnabled) return base;

    return [
      ...base,
      {
        label: (
          <>
            <HiOutlineBuildingOffice2 size={16} />
            <span>Entity</span>
          </>
        ),
        content: null,
      },
    ];
  }, [profilesEnabled]);

  return (
    <Box bg="white" borderRadius="16px" p={5} overflow="hidden">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex gap="8" alignItems={"center"}>
          <H2 fontSize="3">Application Types</H2>

          {!billingInfoLoading &&
            billingInfo?.mandate_type_created &&
            billingInfo?.mandate_type_limit && (
              <H2 color={"gray.60"} fontSize="3">
                {billingInfo.mandate_type_created}/
                {billingInfo.mandate_type_limit}
              </H2>
            )}

          <Tooltip
            position="bottom"
            content="Check our plans and upgrade in order to unlock additional checks that you can add to your application type."
          >
            <Icon Type={IoMdInformationCircleOutline} aria-hidden="true" />
          </Tooltip>
        </Flex>

        {billingInfo?.billing_type === "Basic" &&
        billingInfo?.mandate_type_created >= 2 ? (
          <Tooltip
            position="bottom"
            content="You can only create two applications. To create more templates, please upgrade your plan."
          >
            <Button
              Icon={FaPlus}
              iconSize="16px"
              onClick={() => navigate(ROUTE.SETTINGS_APPLICATION_TYPE_NEW)}
              isDisabled={
                billingInfo?.billing_type === "Basic" &&
                billingInfo?.mandate_type_created >= 2
              }
            >
              New Application Type
            </Button>
          </Tooltip>
        ) : (
          <Button
            Icon={FaPlus}
            iconSize="16px"
            onClick={() => navigate(ROUTE.SETTINGS_APPLICATION_TYPE_NEW)}
          >
            New Application Type
          </Button>
        )}
      </Flex>

      <Paragraph
        fontWeight="500"
        fontSize={1}
        color={"gray.60"}
        mt={"0"}
        mb={3}
      >
        Manage and customise application types to fit your specific business
        needs.
        <Link
          fontSize="1"
          href={env.verifyApplicationTypesLearnMoreUrl}
          target="_blank"
          ml={2}
        >
          Learn more
        </Link>
      </Paragraph>

      <CustomTabs
        activeTab={activeTab}
        onTabChange={(i: number) => setActiveTab(i)}
        customMarginLeft="0"
        variant="segmented"
        tabs={tabs}
        rightSlot={
          <Flex gap="8" alignItems="center" flexWrap="wrap" ml="auto">
            <Select
              width="220px"
              size="regular"
              isInModal
              isMulti
              checkboxMulti
              options={divisionOptions}
              value={divisionOptions.filter((o) =>
                divisionIds.includes(Number(o.value)),
              )}
              onChange={(o) =>
                setDivisionIds(
                  ((o as Option[]) ?? []).map((opt) => Number(opt.value)),
                )
              }
              placeholder="All divisions"
            />
            {hasScopeFilter && (
              <Button variant="text" onClick={() => setDivisionIds([])}>
                Clear
              </Button>
            )}
          </Flex>
        }
      />

      {isLoading ? (
        <Flex justifyContent="center" alignItems="center" minHeight="25vh">
          <Loader />
        </Flex>
      ) : filtered.length === 0 ? (
        <Paragraph color="gray.60" mt={3}>
          {hasScopeFilter
            ? `No ${viewType} application types for this division.`
            : `No ${viewType} application types found.`}
        </Paragraph>
      ) : (
        <Grid>
          {filtered.map((applicationType: any) => (
            <ApplicationTypeCard
              key={applicationType.id}
              _type={applicationType._type}
              id={applicationType.id}
              name={applicationType.name}
              checkpoint={applicationType?.checkpoint}
              created_on={applicationType?.created_on}
              is_disabled={applicationType?.is_disabled}
            />
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SettingsApplicationTypes;
